import { type FormEvent, useEffect, useId, useState } from "react";
import { TASK_CATEGORIES, type TaskCategory } from "../../../types/journey";
import { DatePickerInput } from "../../form/DatePickerInput";
import styles from "./AddTaskModal.module.css";

export interface NewTaskInput {
	category: TaskCategory;
	title: string;
	description: string;
	dueDate: Date | null;
}

export interface AddTaskModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (next: NewTaskInput) => void;
	labels: {
		title: string;
		fieldType: string;
		fieldTitle: string;
		fieldDescription: string;
		fieldDueDate: string;
		typeOptions: Record<TaskCategory, string>;
		save: string;
		cancel: string;
		requiredError: (fieldLabel: string) => string;
		datePickerPlaceholder?: string;
	};
}

export function AddTaskModal({
	open,
	onClose,
	onSave,
	labels,
}: AddTaskModalProps) {
	const headingId = useId();
	const [category, setCategory] = useState<TaskCategory | "">("");
	const [titleValue, setTitleValue] = useState("");
	const [description, setDescription] = useState("");
	const [dueDate, setDueDate] = useState<Date | null>(null);
	const [submitted, setSubmitted] = useState(false);

	// Reset state on close so the next open is clean
	useEffect(() => {
		if (!open) {
			setCategory("");
			setTitleValue("");
			setDescription("");
			setDueDate(null);
			setSubmitted(false);
		}
	}, [open]);

	// ESC key closes modal
	useEffect(() => {
		if (!open) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [open, onClose]);

	if (!open) return null;

	const categoryError = submitted && category === "";
	const titleError = submitted && titleValue.trim() === "";
	const canSave = category !== "" && titleValue.trim() !== "";

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSubmitted(true);
		const trimmedTitle = titleValue.trim();
		if (category === "" || trimmedTitle === "") return;
		onSave({
			category,
			title: trimmedTitle,
			description: description.trim(),
			dueDate,
		});
	};

	return (
		<>
			<button
				type="button"
				className={styles.backdrop}
				onClick={onClose}
				aria-label={labels.cancel}
			/>
			<div className={styles.wrapper}>
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby={headingId}
					className={styles.dialog}
				>
					<h2 id={headingId} className={styles.heading}>
						{labels.title}
					</h2>

					<form onSubmit={handleSubmit} noValidate className={styles.form}>
						<div className={styles.field}>
							<label className={styles.label}>
								{labels.fieldType}
								<select
									className={styles.select}
									value={category}
									onChange={(e) =>
										setCategory(e.target.value as TaskCategory | "")
									}
									aria-invalid={categoryError}
								>
									<option value="" disabled>
										{labels.fieldType}
									</option>
									{TASK_CATEGORIES.map((cat) => (
										<option key={cat} value={cat}>
											{labels.typeOptions[cat]}
										</option>
									))}
								</select>
							</label>
							{categoryError ? (
								<p className={styles.error}>
									{labels.requiredError(labels.fieldType)}
								</p>
							) : null}
						</div>

						<div className={styles.field}>
							<label className={styles.label}>
								{labels.fieldTitle}
								<input
									type="text"
									className={styles.input}
									value={titleValue}
									onChange={(e) => setTitleValue(e.target.value)}
									aria-invalid={titleError}
									maxLength={120}
								/>
							</label>
							{titleError ? (
								<p className={styles.error}>
									{labels.requiredError(labels.fieldTitle)}
								</p>
							) : null}
						</div>

						<div className={styles.field}>
							<label className={styles.label}>
								{labels.fieldDescription}
								<textarea
									className={styles.textarea}
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={3}
									maxLength={500}
								/>
							</label>
						</div>

						<div className={styles.field}>
							<span className={styles.label}>{labels.fieldDueDate}</span>
							<DatePickerInput
								value={dueDate}
								onChange={setDueDate}
								placeholder={labels.datePickerPlaceholder}
							/>
						</div>

						<div className={styles.actions}>
							<button
								type="button"
								className={styles.cancelButton}
								onClick={onClose}
							>
								{labels.cancel}
							</button>
							<button
								type="submit"
								className={styles.saveButton}
								disabled={!canSave}
							>
								{labels.save}
							</button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
}
